import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  Plus,
  Pencil,
  Trash,
  MagnifyingGlass,
  SpinnerGap,
  X,
  Upload,
  CaretLeft,
  CaretRight
} from '@phosphor-icons/react';
import { cn } from '../../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { id: 'alertness', name: 'Alertness' },
  { id: 'attitude', name: 'Attitude' },
  { id: 'safety', name: 'Safety & Your Vehicle' },
  { id: 'margins', name: 'Safety Margins' },
  { id: 'hazard', name: 'Hazard Awareness' },
  { id: 'vulnerable', name: 'Vulnerable Road Users' },
  { id: 'conditions', name: 'Other Conditions' },
  { id: 'documents', name: 'Vehicle Documents' },
  { id: 'accidents', name: 'Incidents & Accidents' },
  { id: 'loading', name: 'Vehicle Loading' },
  { id: 'signs', name: 'Road Signs' },
  { id: 'motorway', name: 'Motorway Rules' },
];

export const AdminQuestionsPage = () => {
  const [questions, setQuestions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const limit = 20;

  useEffect(() => {
    fetchQuestions();
  }, [page, categoryFilter]);

  const fetchQuestions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        skip: page * limit,
        limit: limit,
      });
      if (categoryFilter) params.append('category', categoryFilter);
      if (search) params.append('search', search);
      
      const { data } = await axios.get(`${API}/admin/questions?${params}`, { withCredentials: true });
      setQuestions(data.questions);
      setTotal(data.total);
    } catch (error) {
      console.error('Failed to fetch questions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    fetchQuestions();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this question?')) return;
    
    try {
      await axios.delete(`${API}/admin/questions/${id}`, { withCredentials: true });
      fetchQuestions();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete question');
    }
  };

  const handleEdit = (question) => {
    setEditingQuestion(question);
    setShowModal(true);
  };

  const handleCreate = () => {
    setEditingQuestion(null);
    setShowModal(true);
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="p-6 md:p-8" data-testid="admin-questions">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading font-black text-3xl tracking-tighter">Questions</h1>
          <p className="text-muted-foreground">{total} questions in database</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            className="rounded-sm"
            onClick={() => setShowBulkImport(true)}
          >
            <Upload className="w-4 h-4 mr-2" />
            Bulk Import
          </Button>
          <Button className="rounded-sm" onClick={handleCreate} data-testid="add-question-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Question
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-card border border-border rounded-sm p-4 mb-6">
        <form onSubmit={handleSearch} className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search questions..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 rounded-sm"
              />
            </div>
          </div>
          <select
            value={categoryFilter}
            onChange={(e) => { setCategoryFilter(e.target.value); setPage(0); }}
            className="px-3 py-2 bg-background border border-input rounded-sm text-sm"
          >
            <option value="">All Categories</option>
            {CATEGORIES.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
          <Button type="submit" variant="secondary" className="rounded-sm">
            Search
          </Button>
        </form>
      </div>

      {/* Questions Table */}
      <div className="bg-card border border-border rounded-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <SpinnerGap className="w-8 h-8 text-primary animate-spin mx-auto" />
          </div>
        ) : questions.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            No questions found
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-muted">
              <tr>
                <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-muted-foreground">Question</th>
                <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-muted-foreground w-32">Category</th>
                <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-muted-foreground w-24">Difficulty</th>
                <th className="text-right p-4 text-xs font-bold uppercase tracking-wider text-muted-foreground w-24">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {questions.map(q => (
                <tr key={q.id} className="hover:bg-muted/50">
                  <td className="p-4">
                    <p className="text-sm line-clamp-2">{q.question_text}</p>
                  </td>
                  <td className="p-4">
                    <span className="text-xs bg-muted px-2 py-1 rounded-sm capitalize">{q.category}</span>
                  </td>
                  <td className="p-4">
                    <span className={cn(
                      "text-xs px-2 py-1 rounded-sm capitalize",
                      q.difficulty === 'easy' && "bg-success/20 text-success",
                      q.difficulty === 'medium' && "bg-amber-500/20 text-amber-500",
                      q.difficulty === 'hard' && "bg-destructive/20 text-destructive"
                    )}>
                      {q.difficulty}
                    </span>
                  </td>
                  <td className="p-4 text-right">
                    <button
                      onClick={() => handleEdit(q)}
                      className="p-2 hover:bg-muted rounded-sm transition-colors text-muted-foreground hover:text-foreground"
                      data-testid={`edit-question-${q.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(q.id)}
                      className="p-2 hover:bg-destructive/10 rounded-sm transition-colors text-muted-foreground hover:text-destructive"
                      data-testid={`delete-question-${q.id}`}
                    >
                      <Trash className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t border-border">
            <p className="text-sm text-muted-foreground">
              Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="rounded-sm"
              >
                <CaretLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => p + 1)}
                disabled={page >= totalPages - 1}
                className="rounded-sm"
              >
                <CaretRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Question Modal */}
      {showModal && (
        <QuestionModal
          question={editingQuestion}
          onClose={() => setShowModal(false)}
          onSave={() => { setShowModal(false); fetchQuestions(); }}
        />
      )}

      {/* Bulk Import Modal */}
      {showBulkImport && (
        <BulkImportModal
          onClose={() => setShowBulkImport(false)}
          onSave={() => { setShowBulkImport(false); fetchQuestions(); }}
        />
      )}
    </div>
  );
};

const QuestionModal = ({ question, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    question_text: question?.question_text || '',
    options: question?.options || ['', '', '', ''],
    correct_answer: question?.correct_answer ?? 0,
    explanation: question?.explanation || '',
    category: question?.category || 'alertness',
    difficulty: question?.difficulty || 'medium',
    image_url: question?.image_url || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      if (question) {
        await axios.put(`${API}/admin/questions/${question.id}`, formData, { withCredentials: true });
      } else {
        await axios.post(`${API}/admin/questions`, formData, { withCredentials: true });
      }
      onSave();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to save question');
    } finally {
      setSaving(false);
    }
  };

  const updateOption = (index, value) => {
    const newOptions = [...formData.options];
    newOptions[index] = value;
    setFormData({ ...formData, options: newOptions });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-card border border-border rounded-sm w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="font-heading font-bold text-lg">
            {question ? 'Edit Question' : 'Add Question'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-muted rounded-sm">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <Label>Question Text</Label>
            <textarea
              value={formData.question_text}
              onChange={(e) => setFormData({ ...formData, question_text: e.target.value })}
              required
              rows={3}
              className="w-full mt-1 px-3 py-2 bg-background border border-input rounded-sm text-sm resize-none"
            />
          </div>

          <div>
            <Label>Options</Label>
            <div className="space-y-2 mt-1">
              {formData.options.map((opt, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="correct_answer"
                    checked={formData.correct_answer === idx}
                    onChange={() => setFormData({ ...formData, correct_answer: idx })}
                    className="accent-primary"
                  />
                  <Input
                    value={opt}
                    onChange={(e) => updateOption(idx, e.target.value)}
                    placeholder={`Option ${idx + 1}`}
                    required
                    className="flex-1 rounded-sm"
                  />
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Select the correct answer</p>
          </div>

          <div>
            <Label>Explanation</Label>
            <textarea
              value={formData.explanation}
              onChange={(e) => setFormData({ ...formData, explanation: e.target.value })}
              required
              rows={2}
              className="w-full mt-1 px-3 py-2 bg-background border border-input rounded-sm text-sm resize-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Category</Label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full mt-1 px-3 py-2 bg-background border border-input rounded-sm text-sm"
              >
                {CATEGORIES.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Difficulty</Label>
              <select
                value={formData.difficulty}
                onChange={(e) => setFormData({ ...formData, difficulty: e.target.value })}
                className="w-full mt-1 px-3 py-2 bg-background border border-input rounded-sm text-sm"
              >
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
              </select>
            </div>
          </div>

          <div>
            <Label>Image URL (optional)</Label>
            <Input
              value={formData.image_url}
              onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
              placeholder="https://..."
              className="mt-1 rounded-sm"
            />
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t border-border">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-sm">
              Cancel
            </Button>
            <Button type="submit" disabled={saving} className="rounded-sm">
              {saving ? <SpinnerGap className="w-4 h-4 animate-spin" /> : 'Save Question'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

const BulkImportModal = ({ onClose, onSave }) => {
  const [jsonData, setJsonData] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    
    try {
      const questions = JSON.parse(jsonData);
      if (!Array.isArray(questions)) {
        throw new Error('Data must be an array of questions');
      }
      
      await axios.post(`${API}/admin/questions/bulk`, { questions }, { withCredentials: true });
      onSave();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Invalid JSON format');
    } finally {
      setSaving(false);
    }
  };

  const sampleData = JSON.stringify([{
    "question_text": "What should you do before making a U-turn?",
    "options": ["Option A", "Option B", "Correct Option", "Option D"],
    "correct_answer": 2,
    "explanation": "Explanation text here",
    "category": "alertness",
    "difficulty": "medium"
  }], null, 2);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-card border border-border rounded-sm w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="font-heading font-bold text-lg">Bulk Import Questions</h2>
          <button onClick={onClose} className="p-2 hover:bg-muted rounded-sm">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <Label>JSON Data</Label>
            <textarea
              value={jsonData}
              onChange={(e) => setJsonData(e.target.value)}
              required
              rows={10}
              placeholder={sampleData}
              className="w-full mt-1 px-3 py-2 bg-background border border-input rounded-sm text-sm font-mono resize-none"
            />
          </div>

          {error && (
            <p className="text-destructive text-sm">{error}</p>
          )}

          <div className="bg-muted p-3 rounded-sm">
            <p className="text-xs font-bold mb-1">Required fields:</p>
            <p className="text-xs text-muted-foreground">
              question_text, options (array of 4), correct_answer (0-3), explanation, category
            </p>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t border-border">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-sm">
              Cancel
            </Button>
            <Button type="submit" disabled={saving} className="rounded-sm">
              {saving ? <SpinnerGap className="w-4 h-4 animate-spin" /> : 'Import Questions'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
